'use client';

import { useActionState, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { getSafeRedirectPath } from '@/lib/auth-redirect';
import { QrCode, Mail, Lock, User, Eye, EyeOff, ArrowRight, Loader2, Zap } from 'lucide-react';
import { generateSecurePassword } from '@/lib/password';
import { useAuth } from '@/hooks/useAuth';
import { OAuthButtons } from '@/components/auth/OAuthButtons';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import styles from './page.module.css';

type Tab = 'login' | 'register';

interface LoginState {
  error?: string;
  fieldErrors?: Record<string, string>;
}

interface RegisterState {
  error?: string;
  fieldErrors?: Record<string, string>;
}

/**
 * Страница входа / регистрации в стиле shadcn/ui (Светлая тема).
 */
export default function LoginPage() {
  const [tab, setTab]                           = useState<Tab>('login');
  const [showPassword, setShowPassword]         = useState(false);
  const [regPassword, setRegPassword]           = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const { login, register }                     = useAuth();
  const router                          = useRouter();
  const searchParams                    = useSearchParams();
  const redirectPath                    = getSafeRedirectPath(searchParams.get('redirect'));
  const oauthError                      = searchParams.get('oauth_error');
  const emailVerifyError                = searchParams.get('email_verify_error');

  // ── Action: Вход ────────────────────────────────────────────────────────────
  const loginAction = async (prevState: LoginState, formData: FormData): Promise<LoginState> => {
    const login_identity = formData.get('login_identity') as string;
    const password       = formData.get('password') as string;
    const rememberMe     = formData.get('remember_me') === 'on';

    if (!login_identity || !password) {
      return { error: 'Заполните все обязательные поля' };
    }

    const result = await login({ login_identity, password }, rememberMe);

    if (result.success) {
      router.push(redirectPath);
      return {};
    }

    return { error: result.error ?? 'Ошибка входа' };
  };

  // ── Action: Регистрация ──────────────────────────────────────────────────────
  const registerAction = async (prevState: RegisterState, formData: FormData): Promise<RegisterState> => {
    const email    = formData.get('email') as string;
    const password = formData.get('password') as string;

    if (!email || !password) {
      return { error: 'Заполните все обязательные поля' };
    }

    if (password.length < 8) {
      return { fieldErrors: { password: 'Минимум 8 символов' } };
    }

    const result = await register({ email, password, password_confirmation: password });

    if (result.success) {
      router.push(redirectPath);
      return {};
    }

    return { error: result.error ?? 'Ошибка регистрации' };
  };

  const [loginState, loginFormAction, loginPending]       = useActionState(loginAction, {});
  const [registerState, registerFormAction, registerPending] = useActionState(registerAction, {});

  const handleGeneratePassword = () => {
    setRegPassword(generateSecurePassword());
    setShowPassword(true);
  };

  return (
    <div className={styles.page}>
      {/* Фоновое изображение природы */}
      <div 
        className={styles.bgNature} 
        style={{ backgroundImage: "url('/nature_bg.png')" }}
      />
      <div className={styles.bgOverlay} />

      {/* Карточка входа/регистрации в светлом стиле shadcn (используем raw-div для исключения конфликтов стилей) */}
      <div className="relative w-full max-w-[440px] bg-white/90 border border-white/60 text-slate-900 backdrop-blur-xl shadow-2xl shadow-indigo-950/5 z-10 p-2 rounded-2xl animate-fade-in">
        <div className="flex flex-col space-y-1.5 p-6 text-center pb-4 pt-6">
          <div className="flex items-center justify-center gap-2 mb-1">
            <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-tr from-indigo-600 to-violet-500 rounded-xl text-white shadow-lg shadow-indigo-500/30">
              <QrCode className="h-5 w-5" />
            </div>
            <div className="flex items-baseline text-2xl font-bold tracking-tight">
              <span className="text-slate-900">ucheta</span>
              <span className="text-indigo-600">.ru</span>
            </div>
          </div>
          <div className="text-slate-500 text-xs font-semibold uppercase tracking-wider">
            Учёт ТМЦ для современного бизнеса
          </div>
          <div className="pt-1 text-sm font-bold text-amber-700">
            Бета версия — только для тестов
          </div>
        </div>

        {/* Переключатель табов в светлом стиле */}
        <div className="px-6 pb-4">
          <div className="grid grid-cols-2 p-1 bg-slate-100/80 border border-slate-200/80 rounded-xl">
            <Button
              type="button"
              variant={tab === 'login' ? 'secondary' : 'ghost'}
              className={`h-9 rounded-lg font-semibold text-xs transition-all ${
                tab === 'login' 
                  ? 'bg-white text-slate-900 shadow-sm border border-slate-200/80' 
                  : 'text-slate-500 hover:text-slate-900 hover:bg-white/50'
              }`}
              onClick={() => setTab('login')}
            >
              Вход
            </Button>
            <Button
              type="button"
              variant={tab === 'register' ? 'secondary' : 'ghost'}
              className={`h-9 rounded-lg font-semibold text-xs transition-all ${
                tab === 'register' 
                  ? 'bg-white text-slate-900 shadow-sm border border-slate-200/80' 
                  : 'text-slate-500 hover:text-slate-900 hover:bg-white/50'
              }`}
              onClick={() => setTab('register')}
            >
              Регистрация
            </Button>
          </div>
        </div>

        <div className="p-6 pt-0 space-y-4 px-6 pb-6">
          {oauthError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs animate-shake">
              <span>⚠</span> {oauthError}
            </div>
          )}
          {emailVerifyError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs animate-shake">
              <span>⚠</span> {emailVerifyError}
            </div>
          )}

          {/* ── ФОРМА ВХОДА ──────────────────────────────────────────────────── */}
          {tab === 'login' && (
            <form action={loginFormAction} className="space-y-4" noValidate>
              {loginState.error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs animate-shake">
                  <span>⚠</span> {loginState.error}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="login-identity" className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Логин или Email
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="login-identity"
                    name="login_identity"
                    type="text"
                    autoComplete="username"
                    className="h-10 pl-10 bg-white/70 border-slate-200 text-slate-900 focus-visible:border-indigo-500 focus-visible:ring-indigo-500/10"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="login-password" className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Пароль
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="login-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="h-10 pl-10 pr-10 bg-white/70 border-slate-200 text-slate-900 focus-visible:border-indigo-500 focus-visible:ring-indigo-500/10"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-slate-400 hover:text-slate-900 hover:bg-slate-100/50"
                    onClick={() => setShowPassword(v => !v)}
                    aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Запомнить меня + Забыли пароль */}
              <div className="flex items-center justify-between gap-3 pt-1 pb-1">
                <label className="flex items-center gap-2 cursor-pointer group select-none">
                  <input
                    type="checkbox"
                    name="remember_me"
                    id="remember_me"
                    defaultChecked
                    className="rounded border-slate-300 bg-white text-indigo-600 focus:ring-indigo-500 focus:ring-offset-white size-4 cursor-pointer accent-indigo-600"
                  />
                  <span className="text-xs font-semibold text-slate-500 group-hover:text-slate-700 transition-colors">
                    Запомнить меня
                  </span>
                </label>
                <Link
                  href="/forgot-password"
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-500 hover:underline whitespace-nowrap"
                  id="link-forgot-password"
                >
                  Забыли пароль?
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full h-10 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white font-semibold rounded-xl shadow-lg shadow-indigo-600/20 transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50"
                disabled={loginPending}
                id="btn-login-submit"
              >
                {loginPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Вход...</>
                ) : (
                  <>Войти <ArrowRight className="ml-2 h-4 w-4" /></>
                )}
              </Button>
            </form>
          )}

          {/* ── ФОРМА РЕГИСТРАЦИИ ─────────────────────────────────────────────── */}
          {tab === 'register' && (
            <form action={registerFormAction} className="space-y-4" noValidate>
              {registerState.error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs animate-shake">
                  <span>⚠</span> {registerState.error}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="reg-email" className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Email <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="reg-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    className={`h-10 pl-10 bg-white/70 border-slate-200 text-slate-900 focus-visible:border-indigo-500 focus-visible:ring-indigo-500/10 ${
                      registerState.fieldErrors?.email ? 'border-red-300 focus-visible:ring-red-200' : ''
                    }`}
                    required
                  />
                </div>
                {registerState.fieldErrors?.email && (
                  <span className="text-xs text-red-500">{registerState.fieldErrors.email}</span>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="reg-password" className="text-[11px] font-bold uppercase tracking-wider text-slate-600">
                  Пароль <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="reg-password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    className={`h-10 pl-10 pr-16 bg-white/70 border-slate-200 text-slate-900 focus-visible:border-indigo-500 focus-visible:ring-indigo-500/10 ${
                      registerState.fieldErrors?.password ? 'border-red-300 focus-visible:ring-red-200' : ''
                    }`}
                    required
                  />
                  <input
                    type="password"
                    name="password_confirmation"
                    autoComplete="new-password"
                    value={regPassword}
                    readOnly
                    tabIndex={-1}
                    aria-hidden="true"
                    className="sr-only"
                  />
                  <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-slate-400 hover:text-amber-600 hover:bg-amber-50/80"
                      onClick={handleGeneratePassword}
                      aria-label="Сгенерировать пароль"
                      title="Сгенерировать пароль"
                    >
                      <Zap className="h-4 w-4 shrink-0" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-slate-400 hover:text-slate-900 hover:bg-slate-100/50"
                      onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4 shrink-0" /> : <Eye className="h-4 w-4 shrink-0" />}
                    </Button>
                  </div>
                </div>
                {registerState.fieldErrors?.password && (
                  <span className="text-xs text-red-500">{registerState.fieldErrors.password}</span>
                )}
              </div>

              <p className="text-[10px] text-slate-500 leading-normal pt-1">
                Нажимая «Создать аккаунт», вы соглашаетесь с{' '}
                <a href="/terms" className="text-indigo-600 hover:underline font-medium">Условиями использования</a>{' '}
                и{' '}
                <a href="/privacy" className="text-indigo-600 hover:underline font-medium">Политикой конфиденциальности</a>.
                <br />
                После регистрации вы сразу войдёте в систему. На email придёт ссылка для подтверждения почты — её нужно
                открыть в течение 3 дней.
              </p>

              <Button
                type="submit"
                className="w-full h-10 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white font-semibold rounded-xl shadow-lg shadow-indigo-600/20 transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50"
                disabled={registerPending}
                id="btn-register-submit"
              >
                {registerPending ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Создание аккаунта...</>
                ) : (
                  <>Создать аккаунт <ArrowRight className="ml-2 h-4 w-4" /></>
                )}
              </Button>
            </form>
          )}

          <OAuthButtons redirectPath={redirectPath} />
        </div>
      </div>
    </div>
  );
}
